import { todayLocalISO } from "@/lib/dates";

export type TripStatusType = "upcoming" | "d-day" | "in-progress" | "completed";

export type TripStatusResult = {
  type: TripStatusType;
  label: string;
  badgeText: string;
  diffDays?: number;
  currentDayIndex?: number;
  variant: "default" | "secondary" | "outline";
  className?: string;
};

/**
 * Calculates trip D-Day countdown and status based on today's local date.
 * If baseToday is provided (e.g. for testing), it uses that date instead of todayLocalISO().
 */
export function calculateTripStatus(
  startDate: string,
  endDate: string,
  baseToday: string = todayLocalISO()
): TripStatusResult {
  const todayMs = Date.parse(`${baseToday}T00:00:00Z`);
  const startMs = Date.parse(`${startDate}T00:00:00Z`);
  const endMs = Date.parse(`${endDate}T00:00:00Z`);

  if (baseToday < startDate) {
    const diffDays = Math.round((startMs - todayMs) / 86400000);
    return {
      type: "upcoming",
      label: `여행까지 ${diffDays}일 남음`,
      badgeText: `D-${diffDays}`,
      diffDays,
      variant: "default",
      className: "bg-primary text-primary-foreground font-semibold",
    };
  }

  if (baseToday === startDate) {
    return {
      type: "d-day",
      label: "드디어 여행 당일 (Day 1)!",
      badgeText: "D-Day",
      currentDayIndex: 1,
      variant: "default",
      className: "bg-amber-500 text-white font-bold hover:bg-amber-600 animate-pulse",
    };
  }

  if (baseToday <= endDate) {
    const currentDayIndex = Math.round((todayMs - startMs) / 86400000) + 1;
    return {
      type: "in-progress",
      label: `즐거운 여행 중 (Day ${currentDayIndex})`,
      badgeText: `여행 중 · Day ${currentDayIndex}`,
      currentDayIndex,
      variant: "secondary",
      className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold border-emerald-500/30",
    };
  }

  return {
    type: "completed",
    label: "여행 완료",
    badgeText: "여행 완료",
    variant: "outline",
    className: "text-muted-foreground",
  };
}
